/**
 * Password Validator following NIST 800-63B guidance
 * - Min 12 characters (fintech-appropriate)
 * - Max 128 characters (allows passphrases and password managers)
 * - All characters allowed (spaces, symbols, unicode)
 * - No composition rules (length beats complexity)
 * - Checks against breached passwords via Have I Been Pwned API
 *
 * Breach checking strategy:
 * 1. Format validation (length check, instant)
 * 2. Have I Been Pwned API FIRST (850M+ breached passwords, ~100-500ms)
 * 3. If API fails → Check common passwords list (~250 passwords, instant fallback)
 * 4. If both fail → Graceful degradation (allow password - don't block users)
 */

import crypto from "crypto";
import { isCommonPassword } from "./common-passwords";

const MIN_PASSWORD_LENGTH = 12;
const MAX_PASSWORD_LENGTH = 128;

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface PasswordStrengthCheck {
  isChecking: boolean;
  isPwned: boolean | null;
  pwnedCount: number | null;
  error: string | null;
}

/**
 * Validates password format against NIST 800-63B requirements
 * Only checks length - no character restrictions
 */
export function validatePasswordFormat(
  password: string,
): PasswordValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!password) {
    errors.push("Password is required.");
    return { isValid: false, errors, warnings };
  }

  if (password.length < MIN_PASSWORD_LENGTH) {
    errors.push(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
  }

  if (password.length > MAX_PASSWORD_LENGTH) {
    errors.push(`Password must not exceed ${MAX_PASSWORD_LENGTH} characters.`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Checks if password has been breached
 *
 * Strategy (API FIRST):
 * 1. Try Have I Been Pwned API (always up-to-date, 850M+ breached passwords)
 * 2. If API fails → Check common passwords list (~250 passwords as fallback)
 * 3. If both fail → Graceful degradation (allow password - don't block users)
 */
export async function checkPasswordBreach(password: string): Promise<{
  isPwned: boolean;
  pwnedCount: number;
  error: string | null;
}> {
  try {
    // STEP 1: Try API first (primary method)
    const apiResult = await checkPasswordBreachAPI(password);

    // If API succeeded (no error), use its result
    if (!apiResult.error) {
      return apiResult;
    }

    // STEP 2: API failed, check common passwords list as fallback
    console.warn(
      "haveibeenpwned API unavailable, checking common passwords list:",
      apiResult.error,
    );

    if (isCommonPassword(password)) {
      return {
        isPwned: true,
        pwnedCount: 0, // We don't have count for common passwords
        error: null,
      };
    }

    // STEP 3: Both API and common passwords check passed, allow password
    // console.warn(
    //   "API unavailable but password not in common list, allowing password",
    // );
    return {
      isPwned: false,
      pwnedCount: 0,
      error: "Unable to verify password against breach database",
    };
  } catch (error) {
    // Unexpected error, gracefully degrade
    console.error("Password breach check error:", error);
    return {
      isPwned: false,
      pwnedCount: 0,
      error:
        error instanceof Error
          ? error.message
          : "Unable to check password breach database",
    };
  }
}

/**
 * Check password against Have I Been Pwned API (k-anonymity model)
 * Uses k-anonymity to protect password privacy - only first 5 chars of hash are sent
 */
async function checkPasswordBreachAPI(password: string): Promise<{
  isPwned: boolean;
  pwnedCount: number;
  error: string | null;
}> {
  try {
    // SHA-1 hash the password
    const hash = crypto
      .createHash("sha1")
      .update(password)
      .digest("hex")
      .toUpperCase();

    // Use k-anonymity: send only first 5 chars of hash
    const prefix = hash.substring(0, 5);
    const suffix = hash.substring(5);

    // Call Have I Been Pwned API with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    const response = await fetch(
      `https://api.pwnedpasswords.com/range/${prefix}`,
      {
        headers: {
          "User-Agent": "Repayas-Password-Checker",
        },
        signal: controller.signal,
      },
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      // API returned error - return error for graceful degradation
      return {
        isPwned: false,
        pwnedCount: 0,
        error: `API returned ${response.status}`,
      };
    }

    const text = await response.text();
    const hashes = text.split("\n");

    // Check if our hash suffix appears in the results
    for (const line of hashes) {
      const [hashSuffix, count] = line.split(":");
      if (hashSuffix === suffix) {
        return { isPwned: true, pwnedCount: parseInt(count, 10), error: null };
      }
    }

    return { isPwned: false, pwnedCount: 0, error: null };
  } catch (error) {
    // Network error or timeout - return error for graceful degradation
    return {
      isPwned: false,
      pwnedCount: 0,
      error: error instanceof Error ? error.message : "API request failed",
    };
  }
}

/**
 * Client-side version of breach check (uses browser crypto API)
 *
 * Strategy (API FIRST):
 * 1. Try Have I Been Pwned API (always up-to-date)
 * 2. If API fails → Check common passwords list as fallback
 * 3. If both fail → Graceful degradation
 */
export async function checkPasswordBreachClient(password: string): Promise<{
  isPwned: boolean;
  pwnedCount: number;
  error: string | null;
}> {
  try {
    console.log("checkPasswordBreachClient called");

    // Check if Web Crypto API is available (use window.crypto or globalThis.crypto for browser)
    const browserCrypto =
      typeof window !== "undefined" ? window.crypto : globalThis.crypto;

    if (!browserCrypto?.subtle) {
      console.error("crypto.subtle is not available");
      throw new Error("Web Crypto API not available");
    }

    // STEP 1: Try API first (primary method)
    // Use SubtleCrypto API for SHA-1 hashing in browser
    console.log("Hashing password...");
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await browserCrypto.subtle.digest("SHA-1", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hash = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase();

    const prefix = hash.substring(0, 5);
    const suffix = hash.substring(5);

    console.log(
      "Making API request to pwnedpasswords.com with prefix:",
      prefix,
    );
    const response = await fetch(
      `https://api.pwnedpasswords.com/range/${prefix}`,
      {
        headers: {
          "User-Agent": "Repayas-Password-Checker",
        },
      },
    );

    console.log("API response status:", response.status);

    if (!response.ok) {
      // API returned error, fall through to common passwords check
      throw new Error(`API returned ${response.status}`);
    }

    const text = await response.text();
    const hashes = text.split("\n");

    for (const line of hashes) {
      const [hashSuffix, count] = line.split(":");
      if (hashSuffix === suffix) {
        console.log("Password found in breach database! Count:", count);
        return { isPwned: true, pwnedCount: parseInt(count, 10), error: null };
      }
    }

    // Password not found in API, it's safe
    console.log("Password NOT found in breach database - safe!");
    return { isPwned: false, pwnedCount: 0, error: null };
  } catch (error) {
    // STEP 2: API failed, check common passwords list as fallback
    console.warn("API check failed, using common passwords fallback:", error);

    if (isCommonPassword(password)) {
      console.log("Password found in common passwords list");
      return {
        isPwned: true,
        pwnedCount: 0, // We don't have count for common passwords
        error: null,
      };
    }

    // STEP 3: Both checks passed/failed, gracefully degrade
    console.log("Password not in common list, allowing");
    return {
      isPwned: false,
      pwnedCount: 0,
      error:
        error instanceof Error
          ? error.message
          : "Unable to check password breach database",
    };
  }
}

/**
 * Get user-friendly message for password validation
 */
export function getPasswordValidationMessage(
  formatResult: PasswordValidationResult,
  breachCheck?: PasswordStrengthCheck,
): { type: "error" | "warning" | "success"; message: string } | null {
  // Format errors take priority
  if (!formatResult.isValid && formatResult.errors.length > 0) {
    return { type: "error", message: formatResult.errors[0] };
  }

  // Breach check results
  if (breachCheck && !breachCheck.isChecking) {
    if (breachCheck.isPwned) {
      const count = breachCheck.pwnedCount || 0;
      return {
        type: "error",
        message: `This password is too common, please choose a different password.`,
      };
    }

    if (breachCheck.isPwned === false && !breachCheck.error) {
      return { type: "success", message: "Password strength verified" };
    }
  }

  return null;
}
